import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, NEXUS_LOGO } from "../lib/api";
import { safeSet } from "../lib/safeStorage";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import SEO from "../components/SEO";

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendEmail, setResendEmail] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== pw2) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/set-password", { token, password: pw });
      safeSet("nexus_token", data.token);
      await refresh();
      toast.success("Password set — welcome to Nexus");
      navigate("/creator");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't set password"); }
    finally { setBusy(false); }
  };
  const resend = async (e) => {
    e.preventDefault();
    await api.post("/auth/resend-set-password", { email: resendEmail }).catch(() => {});
    toast.success("If that email has a pending account, a new link is on its way");
  };

  return (
    <div className="min-h-screen bg-[#060608] text-white flex items-center justify-center px-4" data-testid="set-password-page">
      <SEO title="Set your password" description="Finish setting up your Nexus account." path="/set-password" noIndex />
      <div className="w-full max-w-md glass rounded-3xl p-8">
        <img src={NEXUS_LOGO} alt="Nexus" className="h-10 w-10 mb-4" />
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">Almost there</div>
        <h1 className="font-display font-bold text-3xl tracking-tighter">Set your Nexus password</h1>
        <p className="text-sm text-zinc-400 mt-2">Your account was created through your agency. Choose a password to open it.</p>
        {token ? (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div><Label className="text-xs uppercase tracking-widest text-zinc-400">New password</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1 bg-white/[0.02] border-white/10" data-testid="set-password-input" /></div>
            <div><Label className="text-xs uppercase tracking-widest text-zinc-400">Confirm password</Label><Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-1 bg-white/[0.02] border-white/10" data-testid="set-password-confirm" /></div>
            <Button type="submit" disabled={busy} className="w-full h-12 rounded-full nexus-gradient text-white border-0 font-semibold" data-testid="set-password-submit">{busy ? "Saving…" : "Set password & open my account"}</Button>
          </form>
        ) : (
          <form onSubmit={resend} className="mt-6 space-y-3" data-testid="resend-form">
            <p className="text-sm text-amber-200">This link is missing its token. Enter your email and we'll send a fresh one.</p>
            <Input type="email" value={resendEmail} onChange={(e) => setResendEmail(e.target.value)} placeholder="you@email.com" className="bg-white/[0.02] border-white/10" data-testid="resend-email" />
            <Button type="submit" variant="outline" className="w-full rounded-full border-white/15 text-white" data-testid="resend-submit">Email me a new link</Button>
          </form>
        )}
        <p className="text-xs text-zinc-500 mt-6">Already set one? <Link to="/auth?mode=login" className="underline hover:text-white">Log in</Link></p>
      </div>
    </div>
  );
}
